import React from 'react'
import styled, {css} from 'styled-components'

import ExternalLink from '@/components/external-link'
import Image from '@/components/image'
import type {Props as RichTextProps} from '@/components/rich-text'
import RichText from '@/components/rich-text'
import {Stars} from '@/components/stars'
import {Layout, Section} from '@/styles/grid'
import type {textVariants} from '@/theme'
import type {ImageSource} from '@/types/image'
import hasNonEmptyItems from '@/utils/has-non-empty-items'
import type {BackgroundColor} from '@/utils/is-valid-background-color'
import mq from '@/utils/media-query'

interface Item {
    icon: ImageSource
    stars?: string | number
    link?: string
    onClick?: () => void
}

export interface Props {
    title: RichTextProps['content']
    items: Item[]
    titleVariant?: keyof typeof textVariants
    highlightColor?: BackgroundColor
    iconHeight?: 60 | 187
    iconWidth?: 112 | 144
    dataTestId?: string
    children?: React.ReactNode
}

const TitleWithIconGridSection = React.forwardRef<HTMLElement, Props>(
    (
        {
            children,
            title,
            items,
            titleVariant = 'title-base',
            highlightColor,
            iconHeight = 60,
            iconWidth = 112,
            dataTestId,
        },
        ref,
    ) => {
        return (
            <Layout
                ref={ref}
                as={Wrapper}
                $variant="two-asymmetrical"
                $alignItems="center"
                $highlightColor={highlightColor}
                data-testid={dataTestId}
            >
                <ContentWrapper>
                    <RichText $variant={titleVariant} forwardedAs="h2" content={title} />

                    {children}
                </ContentWrapper>

                {hasNonEmptyItems(items) && (
                    <Icons $twoCol={items.length <= 4}>
                        {items.map((item, i) => (
                            <IconWrapper
                                as={item.link ? ExternalLink : 'div'}
                                {...(item.link && {
                                    to: item.link,
                                })}
                                {...(item.onClick && {
                                    onClick: item.onClick,
                                })}
                                key={`${item.icon?.url}-${i}`}
                                $extraSpace={iconHeight === 60}
                            >
                                <Icon
                                    image={item.icon}
                                    $height={iconHeight}
                                    $width={iconWidth}
                                    height={iconHeight}
                                />

                                {item.stars && <Stars numberOfStars={Number(item.stars)} />}
                            </IconWrapper>
                        ))}
                    </Icons>
                )}
            </Layout>
        )
    },
)

const Wrapper = styled(Section)<{$highlightColor: Props['highlightColor']}>`
    strong {
        font-weight: ${({theme}) => theme.fonts.weights.normal};
        background: ${({$highlightColor, theme}) =>
            $highlightColor ? theme.colors.background[$highlightColor] : 'inherit'};
    }
`

const ContentWrapper = styled.div`
    display: grid;
    align-content: center;
    gap: ${({theme}) => theme.spacings.s20};
    justify-items: stretch;

    ${mq.min('xs')} {
        gap: ${({theme}) => theme.spacings.s32};
        justify-items: start;
    }
`

interface IconsProps {
    $twoCol?: boolean
}

const twoColStyles = css`
    ${mq.between('md', 'lg')} {
        grid-template-columns: repeat(2, auto);
        justify-content: space-evenly;
    }
`

const Icons = styled.div<IconsProps>`
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: space-between;

    ${mq.min('xs')} {
        gap: ${({theme}) => theme.spacings.s8};
        grid-template-columns: repeat(4, auto);
    }

    ${mq.min('sm')} {
        grid-template-columns: repeat(2, auto);
    }

    ${mq.min('md')} {
        grid-template-columns: repeat(3, auto);
    }

    ${mq.min('lg')} {
        grid-template-columns: repeat(4, auto);
    }

    ${(props) => props.$twoCol && twoColStyles}
`

interface IconWrapperProps {
    $extraSpace: boolean
}

const IconWrapper = styled.div<IconWrapperProps>`
    display: grid;
    justify-items: center;
    gap: ${({theme}) => theme.spacings.s8};
    padding: ${({$extraSpace, theme}) => ($extraSpace ? theme.spacings.s16 : '0px')};

    &:nth-child(n + 9) {
        ${mq.max('md')} {
            display: none;
        }
    }
`

interface IconProps {
    $width?: number | 'auto'
    $height?: number | 'auto'
}

const Icon = styled(Image)<IconProps>`
    width: ${(props) => (props.$width === 'auto' ? 'auto' : `${props.$width}px`)};
    height: ${(props) => (props.$height === 'auto' ? 'auto' : `${props.$height}px`)};
    object-fit: contain;
`

export default TitleWithIconGridSection
